import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en';
import tr, { type Translation } from './locales/tr';
import { homeTitle, LANGUAGE_PATHS } from '@/config/brand';
import { readPreference, writePreference } from '@/lib/preferences';

export const LANGUAGES = [
  { code: 'tr', label: 'Türkçe', short: 'TR' },
  { code: 'en', label: 'English', short: 'EN' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

const isLanguage = (value: unknown): value is LanguageCode => LANGUAGES.some((l) => l.code === value);

/** The language a page address is for (`/tr/…` is Turkish), or null for the root. */
export function languageFromPath(pathname: string): LanguageCode | null {
  for (const language of LANGUAGES) {
    const path = LANGUAGE_PATHS[language.code];
    if (path !== '/' && (pathname === path.slice(0, -1) || pathname.startsWith(path))) return language.code;
  }
  return null;
}

/**
 * An address that names a language wins (a search result for the Turkish page
 * must open in Turkish), then the person's earlier choice, then the browser.
 */
export function detectLanguage(stored: unknown, browserLanguages: readonly string[], pathname = '/'): LanguageCode {
  const fromPath = languageFromPath(pathname);
  if (fromPath) return fromPath;
  if (isLanguage(stored)) return stored;
  for (const language of browserLanguages) {
    const base = language.toLowerCase().split('-')[0];
    if (isLanguage(base)) return base;
  }
  return 'en';
}

const initial = detectLanguage(
  readPreference('language'),
  typeof navigator === 'undefined' ? [] : navigator.languages,
  typeof location === 'undefined' ? '/' : location.pathname,
);

void i18n.use(initReactI18next).init({
  resources: { tr: { translation: tr }, en: { translation: en } },
  lng: initial,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  // Keys such as `format.aspects.9:16` contain colons; we don't use namespaces.
  nsSeparator: false,
  returnNull: false,
});

const LOCALES: Record<LanguageCode, Translation> = { tr, en };

/**
 * Page language, tab title and address follow the chosen language, so a shared
 * or bookmarked link opens in the same language (each has its own crawlable page).
 */
const syncDocumentLanguage = (language: LanguageCode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.title = homeTitle(LOCALES[language].meta.title);
  const path = LANGUAGE_PATHS[language];
  if (location.pathname !== path) history.replaceState(history.state, '', `${path}${location.search}${location.hash}`);
};
syncDocumentLanguage(initial);

export function changeLanguage(language: LanguageCode): void {
  writePreference('language', language);
  syncDocumentLanguage(language);
  void i18n.changeLanguage(language);
}

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: Translation };
  }
}

export default i18n;
